/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";
import {
  getCrossChainStatusDecoder,
  getCrossChainStatusEncoder,
  type CrossChainStatus,
  type CrossChainStatusArgs,
} from "../types";

export const UPDATE_CROSSCHAIN_STATUS_DISCRIMINATOR = new Uint8Array([
  22, 167, 59, 238, 22, 231, 239, 146,
]);

export function getUpdateCrosschainStatusDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    UPDATE_CROSSCHAIN_STATUS_DISCRIMINATOR,
  );
}

export type UpdateCrosschainStatusInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountCredential extends string | AccountMeta<string> = string,
  TAccountIssuer extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountCredential extends string
        ? WritableAccount<TAccountCredential>
        : TAccountCredential,
      TAccountIssuer extends string
        ? WritableSignerAccount<TAccountIssuer> &
            AccountSignerMeta<TAccountIssuer>
        : TAccountIssuer,
      ...TRemainingAccounts,
    ]
  >;

export type UpdateCrosschainStatusInstructionData = {
  discriminator: ReadonlyUint8Array;
  crossmintCredentialId: string;
  status: CrossChainStatus;
};

export type UpdateCrosschainStatusInstructionDataArgs = {
  crossmintCredentialId: string;
  status: CrossChainStatusArgs;
};

export function getUpdateCrosschainStatusInstructionDataEncoder(): Encoder<UpdateCrosschainStatusInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      [
        "crossmintCredentialId",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      ["status", getCrossChainStatusEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: UPDATE_CROSSCHAIN_STATUS_DISCRIMINATOR,
    }),
  );
}

export function getUpdateCrosschainStatusInstructionDataDecoder(): Decoder<UpdateCrosschainStatusInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    [
      "crossmintCredentialId",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
    ["status", getCrossChainStatusDecoder()],
  ]);
}

export function getUpdateCrosschainStatusInstructionDataCodec(): Codec<
  UpdateCrosschainStatusInstructionDataArgs,
  UpdateCrosschainStatusInstructionData
> {
  return combineCodec(
    getUpdateCrosschainStatusInstructionDataEncoder(),
    getUpdateCrosschainStatusInstructionDataDecoder(),
  );
}

export type UpdateCrosschainStatusInput<
  TAccountCredential extends string = string,
  TAccountIssuer extends string = string,
> = {
  credential: Address<TAccountCredential>;
  issuer: TransactionSigner<TAccountIssuer>;
  crossmintCredentialId: UpdateCrosschainStatusInstructionDataArgs["crossmintCredentialId"];
  status: UpdateCrosschainStatusInstructionDataArgs["status"];
};

export function getUpdateCrosschainStatusInstruction<
  TAccountCredential extends string,
  TAccountIssuer extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateCrosschainStatusInput<TAccountCredential, TAccountIssuer>,
  config?: { programAddress?: TProgramAddress },
): UpdateCrosschainStatusInstruction<
  TProgramAddress,
  TAccountCredential,
  TAccountIssuer
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    credential: { value: input.credential ?? null, isWritable: true },
    issuer: { value: input.issuer ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.credential),
      getAccountMeta(accounts.issuer),
    ],
    data: getUpdateCrosschainStatusInstructionDataEncoder().encode(
      args as UpdateCrosschainStatusInstructionDataArgs,
    ),
    programAddress,
  } as UpdateCrosschainStatusInstruction<
    TProgramAddress,
    TAccountCredential,
    TAccountIssuer
  >);
}

export type ParsedUpdateCrosschainStatusInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    credential: TAccountMetas[0];
    issuer: TAccountMetas[1];
  };
  data: UpdateCrosschainStatusInstructionData;
};

export function parseUpdateCrosschainStatusInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedUpdateCrosschainStatusInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { credential: getNextAccount(), issuer: getNextAccount() },
    data: getUpdateCrosschainStatusInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
